import request from '@/common/request'
import type { RespostaDeMensagens, SessaoDoChat } from '@/shareds/chat-de-suporte'

export type TokenDoChatDeSuporte = {
	token: string
	url: string
	expiresInSeconds: number
}

// Token de uso único, assinado pelo backend do ERP: o segredo nunca chega ao navegador. Sem cache de propósito.
// O CNPJ não sai daqui: o servidor escolhe a loja a partir do usuário logado e só usa o lojaId se a loja for dele.
export const buscarTokenChatSuporte = (lojaId?: string | null): Promise<TokenDoChatDeSuporte> =>
	request.get(`/suporte/chat-token`, {
		params: lojaId ? { lojaId } : undefined,
	})

// Se o usuário já viu o destaque de "novo" do chat. Fica no ERP, por usuário, para valer em qualquer computador.
export const buscarNovidadeDoChat = (): Promise<{ vista: boolean }> => request.get(`/suporte/chat-novidade`)

export const marcarNovidadeDoChatComoVista = (): Promise<void> => request.put(`/suporte/chat-novidade`)

export type PedidoDeErroNoChat = {
	codigo: string
	tela?: string | null
	mensagemExibida?: string | null
	lojaId?: string | null
}

// "Falar com o suporte" no diálogo do erro: o backend do ERP acha a ocorrência pelo código no log interno e a registra
// no suporte, servidor a servidor. Só o id opaco volta; o erro em si nunca passa pelo navegador.
export const registrarErroNoChatDeSuporte = (pedido: PedidoDeErroNoChat): Promise<{ reportId: string; expiresAt: string | null }> =>
	request.post(`/suporte/chat-erro`, pedido)

// API do Almode Chat (suporte). Vai por fetch, e não pelo `request` do ERP: lá um 401 encerra a sessão do ERP, e
// aqui ele só quer dizer que a sessão do chat venceu.
const API_DO_CHAT = process.env.VUE_APP_SUPORTE_CHAT_API || ''

export const chatDeSuporteConfigurado = () => !!API_DO_CHAT

export class ErroDoChatDeSuporte extends Error {
	constructor(readonly status: number, readonly codigo: string, mensagem: string) {
		super(mensagem)
		this.name = 'ErroDoChatDeSuporte'
	}
}

type Midia = { s3Key: string; filename?: string; caption?: string }
type ContextoDaTela = { screen: string; detail?: string }

async function chamar<T>(
	caminho: string,
	{ sessao, ...init }: RequestInit & { sessao?: string | null } = {},
): Promise<{ status: number; dados: T | null; etag: string | null }> {
	const headers: Record<string, string> = { ...(init.headers as Record<string, string> | undefined) }
	if (init.body) headers['Content-Type'] = 'application/json'
	if (sessao) headers.Authorization = `Bearer ${sessao}`
	const resposta = await fetch(API_DO_CHAT + caminho, { ...init, headers })
	if (resposta.status === 304) return { status: 304, dados: null, etag: resposta.headers.get('ETag') }
	const json = await resposta.json().catch(() => null)
	if (!resposta.ok) {
		throw new ErroDoChatDeSuporte(
			resposta.status,
			(json && json.error) || `http_${resposta.status}`,
			(json && json.message) || 'Falha no chat.',
		)
	}
	return { status: resposta.status, dados: json as T, etag: resposta.headers.get('ETag') }
}

export const apiDoChatDeSuporte = {
	configuracao: async () => (await chamar<{ allowedOrigins: string[]; pollMs: number }>('/config')).dados,

	abrirSessao: async (token: string) =>
		(await chamar<SessaoDoChat>('/session', { method: 'POST', body: JSON.stringify({ token }) })).dados as SessaoDoChat,

	mensagens: (sessao: string, cursor: string | null, etag: string | null) =>
		chamar<RespostaDeMensagens>(`/messages${cursor ? `?after=${encodeURIComponent(cursor)}` : ''}`, {
			sessao,
			headers: etag ? { 'If-None-Match': etag } : undefined,
		}),

	// Uma parte só por mensagem: texto, arquivo ou o erro anexado (errorReport, o id que o /suporte/chat-erro devolveu)
	enviar: async (
		sessao: string,
		corpo: { clientId: string; text?: string; media?: Midia; errorReport?: string; context?: ContextoDaTela },
	) =>
		(await chamar<{ messageId: number }>('/messages', { method: 'POST', sessao, body: JSON.stringify(corpo) }))
			.dados as { messageId: number },

	responder: async (sessao: string, clientId: string, replyId: string) =>
		(await chamar<{ messageId: number }>('/reply', { method: 'POST', sessao, body: JSON.stringify({ clientId, replyId }) }))
			.dados as { messageId: number },

	marcarComoLidas: async (sessao: string, upTo: number) => {
		await chamar('/read', { method: 'POST', sessao, body: JSON.stringify({ upTo }) })
	},

	pedirEnvioDeArquivo: async (sessao: string, mime: string, size: number) =>
		(await chamar<{ url: string; s3Key: string }>('/uploads', { method: 'POST', sessao, body: JSON.stringify({ mime, size }) }))
			.dados as { url: string; s3Key: string },

	/** PUT do arquivo na URL assinada que o /uploads devolveu (vale 5 minutos). */
	enviarArquivo: async (url: string, mime: string, arquivo: Blob) => {
		const resposta = await fetch(url, { method: 'PUT', headers: { 'Content-Type': mime }, body: arquivo })
		if (!resposta.ok) throw new ErroDoChatDeSuporte(resposta.status, 'upload', 'Não foi possível enviar o arquivo.')
	},
}

/** Id de idempotência do envio: o suporte não duplica a mensagem se a rede repetir. */
export function novoClientId(): string {
	const cripto = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
	if (cripto && cripto.randomUUID) return cripto.randomUUID()
	return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}
